import { PayloadAction } from '@reduxjs/toolkit';
import { selectMaximizedStream } from 'features/layout/features/content/contentSlice';
import { selectActiveSpeaker } from 'features/layout/features/order/orderSlice';
import { SourceDetails } from 'features/layout/types';
import { cleanupMaximizedStreamSaga } from 'features/streaming/sagas/cleanupMaximizedStreamSaga';
import { cleanupAudioStreamSaga } from 'features/streaming/sagas/cleanupAudioStreamSaga';
import { activeSpeakerReset } from 'features/streaming/streamingSlice';
import { StreamingStopped } from 'features/streaming/types';
import { User, UserId } from 'features/users/types';
import { selectActiveUserById, selectLocalUserId } from 'features/users/usersSlice';
import { call, put, select } from 'redux-saga/effects';
import { RTCClient } from 'utils/webrtc';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import { logger } from 'utils/logger';
import { extractMediaStreamState } from 'utils/webrtc/diagnostics/streamStateExtractor';

export function* onStreamingStopSaga(action: PayloadAction<StreamingStopped>) {
  const { mid, feedId, id: userId, type } = action.payload;
  const localUserId: UserId = yield select(selectLocalUserId);

  // Log health check for remote audio streams only (skip local user's streams and video)
  // Video logging provides useful state info, but audio is where we need detailed diagnostics
  if (userId !== localUserId && type === 'audio') {
    const streamMedia = RTCClient.receivingFeed.media.stream(feedId, mid);
    const audioElement = streamMedia?.subscriberMid
      ? RTCClient.receivingFeed.audioElements[streamMedia.subscriberMid]
      : undefined;

    const streamState = extractMediaStreamState(feedId, mid, streamMedia, audioElement);

    logger
      .remote({ system: true, capture: 'streaming' })
      .debug(`[STREAM-HEALTH] streaming-stop received for audio`, {
        userId,
        ...streamState,
      });
  }

  const user: User = yield select(selectActiveUserById, userId);
  if (user) {
    yield call(RTCClient.removeBroadcastingDevice, userId, type);
  }

  const e2eeEnabled: boolean = yield select(selectE2eeEnabled);
  if (e2eeEnabled) {
    yield call(RTCClient.receivingFeed.removeE2eePendingSubscription, {
      userId,
      feedId,
      mid,
    });
  }

  const maximizedStream: SourceDetails = yield select(selectMaximizedStream);

  // this minimize only covers screenshare;
  if (maximizedStream?.userId === userId) {
    yield call(cleanupMaximizedStreamSaga, maximizedStream, action.payload);
  }

  if (action.payload.type === 'video') {
    yield call(RTCClient.receivingFeed.removeStreamTrack, feedId, mid);

    return;
  }

  if (action.payload.type === 'audio') {
    yield call(cleanupAudioStreamSaga, userId, feedId, mid);

    const activeSpeaker: SourceDetails = yield select(selectActiveSpeaker);

    if (
      activeSpeaker?.userId === userId &&
      // prevents from resetting if active speaker stopped screenshare
      // if that screenshare had audio
      activeSpeaker.feedId === feedId
    ) {
      yield put(activeSpeakerReset);
    }
  }
}
